import { useRef, useCallback, useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Underline } from "lucide-react";
import { cn } from "@/lib/utils";

const ALLOWED_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "UL", "OL", "LI", "BR", "DIV", "P", "MARK"]);

const PLACEHOLDER_TAG_RE = /<\/?mark[^>]*>/gi;

/** Wrap [placeholder] tokens with <mark> for in-editor highlighting. */
function addPlaceholderMarks(html: string): string {
  // Don't double-wrap already-marked placeholders.
  const stripped = html.replace(PLACEHOLDER_TAG_RE, "");
  return stripped.replace(/\[[^\][\n]+\]/g, (m) => `<mark>${m}</mark>`);
}

/** Strip <mark> wrappers added by addPlaceholderMarks, restoring clean HTML. */
function stripPlaceholderMarks(html: string): string {
  return html.replace(/<mark>(\[[^\][\n]+\])<\/mark>/gi, "$1");
}

/** Strip inline styles and non-formatting tags from editor HTML. */
function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  function walk(node: Node): void {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        el.removeAttribute("style");
        el.removeAttribute("class");
        if (!ALLOWED_TAGS.has(el.tagName)) {
          // Replace disallowed tag with its children
          while (el.firstChild) el.parentNode!.insertBefore(el.firstChild, el);
          el.remove();
        } else {
          walk(el);
        }
      }
    }
  }
  walk(doc.body);
  return doc.body.innerHTML;
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minWords?: number;
  maxWords?: number;
  className?: string;
  error?: boolean;
  /** Show a "jump to next [placeholder]" helper while the text contains [bracketed] placeholders (used with quick-start templates). */
  placeholderJump?: boolean;
}

const PLACEHOLDER_RE = /\[[^[\]]+\]/g;

export function RichTextEditor({
  value,
  onChange,
  placeholder = "Start typing...",
  minWords = 0,
  maxWords,
  className,
  error = false,
  placeholderJump = false,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);
  const previousValueRef = useRef(value);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    // Use requestAnimationFrame to ensure DOM is updated before reading innerHTML
    requestAnimationFrame(() => {
      if (editorRef.current) {
        onChange(sanitizeHtml(editorRef.current.innerHTML));
      }
    });
  }, [onChange]);

  const plainText = value
    .replace(/<\/(div|p|li|br)\s*>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

  const wordCount = plainText.split(/\s+/).filter(w => w.length > 0).length;
  const charCount = plainText.replace(/\s+/g, " ").length;
  const MAX_CHARS = 5000;
  const isOverMax = charCount > MAX_CHARS;

  const isUnderMin = minWords > 0 && wordCount < minWords;
  const isNearMin = minWords > 0 && wordCount > 0 && wordCount < minWords;
  const isMet = minWords > 0 && wordCount >= minWords;

  const placeholderCount = placeholderJump
    ? (plainText.match(PLACEHOLDER_RE) ?? []).length
    : 0;

  /** Select the next [placeholder] after the caret (wraps to the first one). */
  const jumpToNextPlaceholder = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const sel = window.getSelection();
    // Absolute offset of the caret within the editor's text, to search after it.
    let caretOffset = -1;
    if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
      const r = sel.getRangeAt(0).cloneRange();
      r.selectNodeContents(editor);
      r.setEnd(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
      caretOffset = r.toString().length;
    }
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let offset = 0;
    let first: { node: Text; start: number; end: number } | null = null;
    let next: { node: Text; start: number; end: number } | null = null;
    for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
      PLACEHOLDER_RE.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = PLACEHOLDER_RE.exec(node.data)) !== null) {
        const match = { node, start: m.index, end: m.index + m[0].length };
        if (!first) first = match;
        if (!next && offset + m.index >= caretOffset) next = match;
      }
      offset += node.data.length;
    }
    const target = next ?? first;
    if (!target || !sel) return;
    const range = document.createRange();
    range.setStart(target.node, target.start);
    range.setEnd(target.node, target.end);
    sel.removeAllRanges();
    sel.addRange(range);
    editor.focus();
    target.node.parentElement?.scrollIntoView({ block: "nearest" });
  }, []);

  const tools = [
    { icon: Bold, command: "bold", label: "Bold" },
    { icon: Italic, command: "italic", label: "Italic" },
    { icon: Underline, command: "underline", label: "Underline" },
    { icon: List, command: "insertUnorderedList", label: "Bullet list" },
    { icon: ListOrdered, command: "insertOrderedList", label: "Numbered list" },
  ];

  // Sync external value changes to the editor.
  // The editor displays <mark>-highlighted placeholders; the stored value is always clean.
  // Compare the clean version of the editor's current HTML to avoid resetting the cursor
  // on every keystroke when the parent echoes the same value back.
  useEffect(() => {
    if (editorRef.current) {
      const cleanCurrent = stripPlaceholderMarks(editorRef.current.innerHTML);
      if (cleanCurrent !== value) {
        editorRef.current.innerHTML = addPlaceholderMarks(value);
      }
    }
    previousValueRef.current = value;
    isInternalUpdate.current = false;
  }, [value]);

  return (
    <div className={cn("border rounded-xl overflow-hidden bg-muted/40 transition-colors focus-within:bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1", error ? "border-destructive" : "border-transparent", className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 p-2 border-b border-border bg-muted/30">
        {tools.map(({ icon: Icon, command, label }) => (
          <button
            key={command}
            type="button"
            title={label}
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand(command);
            }}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
      </div>

      {/* Editor — fixed height with scroll; drag the resize handle at the bottom-right to expand */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          document.execCommand("insertText", false, text);
        }}
        onInput={(e) => {
          isInternalUpdate.current = true;
          // Strip mark wrappers before storing so the value stays clean.
          onChange(sanitizeHtml(stripPlaceholderMarks(e.currentTarget.innerHTML)));
        }}
        data-placeholder={placeholder}
        className={cn(
          "h-44 overflow-y-auto p-4 text-sm text-foreground outline-none resize-y",
          "prose prose-sm max-w-none",
          "[&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-muted-foreground [&:empty]:before:pointer-events-none",
          "[&_mark]:bg-yellow-200 [&_mark]:text-yellow-900 [&_mark]:rounded-sm [&_mark]:px-0.5"
        )}
        style={{ minHeight: "11rem", maxHeight: "32rem" }}
      />

      {/* Placeholder jump helper */}
      {placeholderCount > 0 && (
        <div className="flex items-center justify-between px-4 py-1.5 border-t border-border bg-amber-50/60">
          <p className="text-xs text-amber-700">
            {placeholderCount} [placeholder{placeholderCount > 1 ? "s" : ""}] left to fill in
          </p>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); jumpToNextPlaceholder(); }}
            className="text-xs font-medium text-amber-700 hover:text-amber-900 hover:underline cursor-pointer"
          >
            Jump to next →
          </button>
        </div>
      )}

      {/* Word count */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-muted/20">
        <p className={cn(
          "text-xs transition-colors",
          isMet ? "text-green-600 dark:text-green-400" :
          isNearMin ? "text-amber-600 dark:text-amber-400" :
          "text-muted-foreground"
        )}>
          {wordCount} words{minWords > 0 && <> (<span className="text-destructive">min {minWords}</span>)</>}
        </p>
        <p className={cn(
          "text-xs transition-colors",
          isOverMax ? "text-destructive" : "text-muted-foreground"
        )}>
          {charCount.toLocaleString()} / <span className="text-destructive">5,000</span> chars
          {isNearMin && <span className="ml-2 text-amber-600 dark:text-amber-400">· {minWords - wordCount} more words needed</span>}
        </p>
      </div>
    </div>
  );
}
